import React, { Suspense } from 'react'
import HistoryIcon from '@mui/icons-material/History'
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import _ from 'es-toolkit/compat'
import { useTranslation } from 'react-i18next'

import { FormValues } from '../main-types'
import {
  useFeatureFlagDefaults,
  useFeatureFlagDefaultsHistory,
  useUpdateFeatureFlagDefaults,
} from './admin-api'
import { AdminSettings } from './admin-settings-state'
import { FeatureFlagsSection } from './feature-flags-section'
import {
  defaultFormValues,
  getFeatureFlagLabel,
  handleFeatureFlagsChange,
  useFeatureFlagsProps,
} from './use-feature-flags-props'

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString()
}

const LastUpdatedInfo = ({
  createdAt,
  createdBy,
}: {
  createdAt?: string | null
  createdBy?: { firstName: string; lastName: string; email: string } | null
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.feature_flags' })

  if (!createdAt || !createdBy) return null

  return (
    <Typography variant="caption" color="text.secondary">
      {t('last_updated_by', {
        firstName: createdBy.firstName,
        lastName: createdBy.lastName,
        date: formatDate(createdAt),
      })}
    </Typography>
  )
}

const formatChangeValue = (value: unknown): string => {
  if (value === null || value === undefined) return 'not set'
  if (typeof value === 'boolean') return value ? 'enabled' : 'disabled'
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'none'
  if (typeof value === 'string' && value === '') return 'empty'
  if (typeof value === 'string') return getFeatureFlagLabel(value)
  return String(value)
}

export const FeatureFlagHistoryModal = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.feature_flags' })
  const {
    data: history,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useFeatureFlagDefaultsHistory(open)

  const allItems = React.useMemo(
    () => history?.pages.flatMap((page) => page.items) ?? [],
    [history],
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="feature-flag-history-modal"
    >
      <DialogTitle>{t('history_title')}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography color="text.secondary">{t('loading')}</Typography>
          </Box>
        ) : (
          <List dense>
            {allItems.map((item, index) => (
              <ListItem
                key={item.id}
                divider={index < allItems.length - 1}
                sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
              >
                <Box display="flex" justifyContent="space-between" width="100%" mb={0.5}>
                  <Typography variant="body2" fontWeight="medium">
                    {item.createdBy
                      ? `${item.createdBy.firstName} ${item.createdBy.lastName}`
                      : t('system')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(item.createdAt)}
                  </Typography>
                </Box>
                {item.changes.length > 0 ? (
                  <Box component="ul" sx={{ m: 0, pl: 2, width: '100%' }}>
                    {item.changes.map((change, changeIndex) => (
                      <Typography
                        key={changeIndex}
                        component="li"
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 0.25 }}
                      >
                        <strong>{getFeatureFlagLabel(change.field)}</strong>:{' '}
                        {t('change_value_arrow', {
                          oldValue: formatChangeValue(change.oldValue),
                          newValue: formatChangeValue(change.newValue),
                        })}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t('no_changes')}
                  </Typography>
                )}
              </ListItem>
            ))}
            {allItems.length === 0 && (
              <ListItem>
                <ListItemText primary={t('no_history')} />
              </ListItem>
            )}
          </List>
        )}
        {hasNextPage && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              variant="outlined"
              size="small"
            >
              {isFetchingNextPage ? t('loading') : t('load_more')}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('close')}</Button>
      </DialogActions>
    </Dialog>
  )
}

const AdminFeatureFlagDefaultsContent = ({ adminSettings }: { adminSettings: AdminSettings }) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.feature_flags' })
  const { handleClose } = adminSettings
  const updateDefaults = useUpdateFeatureFlagDefaults()
  const { data: flagDefaults } = useFeatureFlagDefaults()

  const [historyOpen, setHistoryOpen] = React.useState(false)

  // Server state (what's saved)
  const serverValues = React.useMemo<FormValues>(() => {
    const result = _.merge({}, defaultFormValues, {
      features: flagDefaults?.features ?? {},
      hasOptionRationale: flagDefaults?.features?.has_option_rationale ?? true,
    })
    result.features.export_file = _.uniq(result.features.export_file ?? [])
    return result
  }, [flagDefaults])

  // Local state (what user is editing)
  const [localValues, setLocalValues] = React.useState<FormValues>(serverValues)

  // Sync local state when server state changes (after save)
  React.useEffect(() => {
    setLocalValues(serverValues)
  }, [serverValues])

  // Check if there are unsaved changes
  const isDirty = React.useMemo(() => {
    return !_.isEqual(localValues, serverValues)
  }, [localValues, serverValues])

  const handleChange = React.useCallback((field: string, value: boolean | string | undefined) => {
    setLocalValues((prev) => handleFeatureFlagsChange(prev, field, value))
  }, [])

  const handleSave = React.useCallback(() => {
    updateDefaults.mutate({
      ...localValues.features,
      has_option_rationale: localValues.hasOptionRationale,
    })
  }, [localValues, updateDefaults])

  const handleDiscard = React.useCallback(() => {
    setLocalValues(serverValues)
  }, [serverValues])

  const { getCheckboxProps, getFilenameConventionProps } = useFeatureFlagsProps({
    values: localValues,
    disabled: updateDefaults.isPending,
    onChange: handleChange,
  })

  return (
    <Box>
      <DialogContent sx={{ pt: '0px !important' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('configure_defaults')}
            </Typography>
            <LastUpdatedInfo
              createdAt={flagDefaults?.createdAt}
              createdBy={flagDefaults?.createdBy}
            />
          </Box>
          <Tooltip title={t('tooltip_view_history')}>
            <IconButton
              onClick={() => setHistoryOpen(true)}
              size="small"
              aria-label={t('aria_view_history')}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {isDirty && (
          <Alert
            severity="warning"
            action={
              <Button size="small" onClick={handleDiscard} sx={{ color: 'warning.contrastText' }}>
                {t('discard')}
              </Button>
            }
          >
            <Typography variant="body2">{t('unsaved_changes')}</Typography>
          </Alert>
        )}

        <FeatureFlagsSection
          getCheckboxProps={getCheckboxProps}
          getFilenameConventionProps={getFilenameConventionProps}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={updateDefaults.isPending}>
          {t('cancel')}
        </Button>
        <Badge color="warning" variant="dot" invisible={!isDirty}>
          <Button
            onClick={handleSave}
            disabled={!isDirty || updateDefaults.isPending}
            variant="contained"
          >
            {updateDefaults.isPending ? t('saving') : t('save_changes')}
          </Button>
        </Badge>
      </DialogActions>

      <React.Suspense fallback={null}>
        <FeatureFlagHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
      </React.Suspense>
    </Box>
  )
}

export const AdminFeatureFlagDefaults = ({ adminSettings }: { adminSettings: AdminSettings }) => (
  <Suspense fallback={<CircularProgress size={24} />}>
    <AdminFeatureFlagDefaultsContent adminSettings={adminSettings} />
  </Suspense>
)
